import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { chatService } from '../services/chatService';
import { conversationService } from '../services/conversationService';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: KnowledgeBaseSource[];
  confidence?: number;
  suggestedActions?: SuggestedAction[];
  requiresEscalation?: boolean;
  actionRequired?: string;
  actionData?: any;
  ticketPreview?: any;
  metadata?: {
    sources?: any[];
    confidence?: string | number;
    suggested_actions?: any[];
    requires_escalation?: boolean;
  };
  feedback?: {
    liked?: boolean;
    disliked?: boolean;
  };
}

export interface Conversation {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
  messages: Message[];
}

export interface KnowledgeBaseSource {
  document_id: string;
  title: string;
  content_snippet: string;
  relevance_score: number;
  url?: string;
}

export interface SuggestedAction {
  action_type: string;
  description: string;
  parameters?: Record<string, any>;
}

interface ChatContextType {
  conversations: Record<string, Conversation>;
  activeConversationId: string | null;
  activeConversation: Conversation | null;
  isConnected: boolean;
  isTyping: boolean;
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  newConversation: () => Promise<string>; // returns id
  selectConversation: (id: string) => void;
  renameConversation: (id: string, title: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  clearMessages: () => void; // clears current conversation
  clearAllConversations: () => Promise<void>; // clears all conversations
  updateMessageFeedback: (messageId: string, feedback: { liked?: boolean; disliked?: boolean }) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState<Record<string, Conversation>>({});
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Storage key per user/tenant
  const storageKey = useMemo(() => {
    if (!user) return null;
    return `chat_conversations_${user.tenant_id}_${user.user_id}`;
  }, [user]);

  // Load conversations: try backend, else restore from localStorage
  useEffect(() => {
    if (user && token) {
      // When using Lambda, we're always "connected" via HTTP API
      setIsConnected(true);
      setError(null);

      // Load conversations from backend
      const loadConversations = async () => {
        try {
          const backendConversations = await conversationService.listConversations(100);
          
          // Convert backend conversations to frontend format
          const frontendConversations: Record<string, Conversation> = {};
          backendConversations.forEach((backendConv) => {
            frontendConversations[backendConv.id] = {
              id: backendConv.id,
              title: backendConv.title,
              createdAt: new Date(backendConv.created_at),
              updatedAt: new Date(backendConv.updated_at),
              messages: backendConv.messages.map((backendMsg) => {
                return {
                  id: backendMsg.id,
                  role: backendMsg.role as 'user' | 'assistant',
                  content: backendMsg.content,
                  timestamp: new Date(backendMsg.timestamp),
                  sources: backendMsg.sources,
                  confidence: backendMsg.confidence,
                  suggestedActions: backendMsg.suggested_actions,
                  requiresEscalation: backendMsg.requires_escalation,
                  // hydrate ticket preview from metadata if present
                  ticketPreview: (backendMsg.metadata && (backendMsg.metadata as any).ticket_preview) || undefined,
                };
              })
            };
          });
          
          setConversations(frontendConversations);
          
          // Set active conversation to the most recent one
          if (backendConversations.length > 0) {
            const mostRecent = backendConversations.sort((a, b) => 
              new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
            )[0];
            setActiveConversationId(mostRecent.id);
          } else {
            // Fallback: restore from localStorage if present
            const saved = storageKey ? localStorage.getItem(storageKey) : null;
            if (saved) {
              try {
                const parsed = JSON.parse(saved) as { conversations: Record<string, Conversation>; activeId: string | null };
                // Rehydrate dates
                const rehydrated: Record<string, Conversation> = {};
                Object.values(parsed.conversations).forEach((c) => {
                  rehydrated[c.id] = {
                    ...c,
                    createdAt: new Date(c.createdAt),
                    updatedAt: new Date(c.updatedAt),
                    messages: c.messages.map((m) => ({ ...m, timestamp: new Date(m.timestamp) })),
                  };
                });
                setConversations(rehydrated);
                setActiveConversationId(parsed.activeId);
              } catch {}
            } else {
              // Create a new conversation if none exist anywhere
              const newConv = await conversationService.createConversation({ title: 'New conversation' });
              const frontendConv: Conversation = {
                id: newConv.id,
                title: newConv.title,
                createdAt: new Date(newConv.created_at),
                updatedAt: new Date(newConv.updated_at),
                messages: []
              };
              setConversations({ [newConv.id]: frontendConv });
              setActiveConversationId(newConv.id);
            }
          }
        } catch (error: any) {
          console.error('Failed to load conversations:', error);
          setError('Failed to load conversations');
          
          // Fallback: create a local conversation
          const id = `${Date.now()}`;
          const conv: Conversation = { id, title: 'New conversation', createdAt: new Date(), updatedAt: new Date(), messages: [] };
          setConversations({ [id]: conv });
          setActiveConversationId(id);
        }
      };

      loadConversations();

      return () => {
        // No cleanup needed for HTTP API
      };
    }
  }, [user, token, storageKey]);

  // Persist conversations locally as safety net (in case backend loses history)
  useEffect(() => {
    if (!storageKey) return;
    try {
      const data = {
        conversations,
        activeId: activeConversationId,
      };
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch {}
  }, [conversations, activeConversationId, storageKey]);

  const activeConversation: Conversation | null = useMemo(() => {
    if (!activeConversationId) return null;
    return conversations[activeConversationId] || null;
  }, [activeConversationId, conversations]);

  const sendMessage = async (content: string) => {
    if (!user || !content.trim()) return;
    
    // Ensure we have an active conversation
    let convId = activeConversationId;
    if (!convId) {
      try {
        console.log('Creating new conversation...');
        const newConv = await conversationService.createConversation({ title: 'New conversation' });
        console.log('Conversation created:', newConv);
        convId = newConv.id;
        const frontendConv: Conversation = {
          id: newConv.id,
          title: newConv.title,
          createdAt: new Date(newConv.created_at),
          updatedAt: new Date(newConv.updated_at),
          messages: []
        };
        setConversations((prev) => ({ ...prev, [convId!]: frontendConv }));
        setActiveConversationId(convId);
      } catch (error: any) {
        console.error('Failed to create conversation:', error);
        setError('Failed to create conversation: ' + error.message);
        return;
      }
    }

    try {
      setIsLoading(true);
      setIsTyping(true);
      setError(null);

      // Add user message to backend
      await conversationService.addMessage(convId!, {
        content: content.trim(),
        role: 'user'
      });

      // Add user message to local state
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };

      setConversations((prev) => {
        const curr = prev[convId!];
        const updated: Conversation = {
          ...curr,
          messages: [...curr.messages, userMessage],
          updatedAt: new Date(),
        };
        return { ...prev, [convId!]: updated };
      });

      // Prepare placeholder for assistant message
      const assistantId = (Date.now() + 1).toString();
      const assistantMessage: Message = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        sources: [],
        confidence: 0,
        suggestedActions: [],
        requiresEscalation: false
      };

      // Add placeholder to state
      setConversations((prev) => {
        const curr = prev[convId!];
        return {
          ...prev,
          [convId!]: {
            ...curr,
            messages: [...curr.messages, assistantMessage],
            updatedAt: new Date(),
          }
        };
      });

      // Use non-streaming endpoint (API Gateway doesn't support Lambda response streaming)
      console.log('📤 ChatContext: Sending message to backend...');
      const currentOrgSlug = localStorage.getItem('org_slug');
      console.log('🔍 ChatContext: Current Org Slug:', currentOrgSlug);

      try {
        const response = await chatService.sendMessage({
          content: content.trim(),
          conversation_history: (activeConversation?.messages || []).slice(-10).map(msg => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp.toISOString(),
          })),
          metadata: { session_id: activeConversationId || undefined }
        });
        console.log('✅ ChatContext: Received response from service:', response);
        console.log('✅ ChatContext: Response content length:', response.content?.length);

        // Update assistant message with response
        setConversations((prev) => {
          const curr = prev[convId!];
          const msgs = [...curr.messages];
          const lastMsg = msgs[msgs.length - 1];
          console.log('🔄 Updating assistant message, last message role:', lastMsg?.role);
          if (lastMsg && lastMsg.role === 'assistant') {
            msgs[msgs.length - 1] = {
              ...lastMsg,
              content: response.content,
              sources: response.sources || [],
              confidence: response.confidence || 0.9,
              ticketPreview: response.ticket_preview,
              actionRequired: response.action_required,
              actionData: response.action_data
            };
            console.log('✅ Updated message with content length:', response.content?.length);
            if (response.ticket_preview) {
              console.log('🎫 Ticket preview present:', response.ticket_preview.ticket_id);
            }
          }
          return {
            ...prev,
            [convId!]: { ...curr, messages: msgs }
          };
        });

        setIsLoading(false);
        setIsTyping(false);

        // Persist the message to backend
        try {
          await conversationService.addMessage(convId!, {
            content: response.content,
            role: 'assistant',
            sources: response.sources || [],
            confidence: response.confidence || 0.9,
            metadata: {
              ticket_preview: response.ticket_preview,
              action_required: response.action_required,
              action_data: response.action_data
            }
          });
        } catch (e) {
          console.error('Failed to persist assistant message:', e);
        }

        // Auto-name conversation if needed
        setConversations((prev) => {
          const curr = prev[convId!];
          if (curr.title === 'New conversation' && curr.messages.length === 2) {
            const newTitle = generateSmartTitle(curr.messages);
            conversationService.updateConversation(convId!, { title: newTitle }).catch(console.error);
            return { 
              ...prev, 
              [convId!]: { ...curr, title: newTitle } 
            };
          }
          return prev;
        });

      } catch (serviceError: any) {
        console.error('❌ ChatContext: Service call failed:', serviceError);
        setError(serviceError.message || 'Failed to get response from AI');
        setIsLoading(false);
        return;
      }
      
    } catch (error: any) {
      setError(error.message || 'Failed to send message');
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const clearMessages = () => {
    if (!activeConversationId) return;
    setConversations((prev) => {
      const curr = prev[activeConversationId];
      if (!curr) return prev;
      const updated: Conversation = { ...curr, messages: [], updatedAt: new Date() };
      return { ...prev, [activeConversationId]: updated };
    });
    setError(null);
  };

  const newConversation = async (): Promise<string> => {
    try {
      const newConv = await conversationService.createConversation({ title: 'New conversation' });
      const frontendConv: Conversation = {
        id: newConv.id,
        title: newConv.title,
        createdAt: new Date(newConv.created_at),
        updatedAt: new Date(newConv.updated_at),
        messages: []
      };
      setConversations((prev) => ({ ...prev, [newConv.id]: frontendConv }));
      setActiveConversationId(newConv.id);
      return newConv.id;
    } catch (error: any) {
      console.error('Failed to create conversation:', error);
      // Fallback to local creation
      const id = `${Date.now()}`;
      const conv: Conversation = { id, title: 'New conversation', createdAt: new Date(), updatedAt: new Date(), messages: [] };
      setConversations((prev) => ({ ...prev, [id]: conv }));
      setActiveConversationId(id);
      return id;
    }
  };

  const selectConversation = (id: string) => {
    if (conversations[id]) setActiveConversationId(id);
  };

  const renameConversation = async (id: string, title: string) => {
    try {
      await conversationService.updateConversation(id, { title });
      setConversations((prev) => {
        const curr = prev[id];
        if (!curr) return prev;
        const updated: Conversation = { ...curr, title, updatedAt: new Date() };
        return { ...prev, [id]: updated };
      });
    } catch (error: any) {
      console.error('Failed to rename conversation:', error);
      // Still update locally for immediate feedback
      setConversations((prev) => {
        const curr = prev[id];
        if (!curr) return prev;
        const updated: Conversation = { ...curr, title, updatedAt: new Date() };
        return { ...prev, [id]: updated };
      });
    }
  };

  const deleteConversation = async (id: string) => {
    try {
      await conversationService.deleteConversation(id);
      setConversations((prev) => {
        const next = { ...prev } as Record<string, Conversation>;
        delete next[id];
        return next;
      });
      if (activeConversationId === id) {
        const remaining = Object.keys(conversations).filter(k => k !== id);
        setActiveConversationId(remaining[0] || null);
      }
    } catch (error: any) {
      console.error('Failed to delete conversation:', error);
      // Still delete locally for immediate feedback
      setConversations((prev) => {
        const next = { ...prev } as Record<string, Conversation>;
        delete next[id];
        return next;
      });
      if (activeConversationId === id) {
        const remaining = Object.keys(conversations).filter(k => k !== id);
        setActiveConversationId(remaining[0] || null);
      }
    }
  };

  const clearAllConversations = async () => {
    try {
      // Delete all conversations from backend
      const conversationIds = Object.keys(conversations);
      await Promise.all(conversationIds.map(id => conversationService.deleteConversation(id)));
      
      setConversations({});
      setActiveConversationId(null);
      setError(null);
    } catch (error: any) {
      console.error('Failed to clear all conversations:', error);
      // Still clear locally for immediate feedback
      setConversations({});
      setActiveConversationId(null);
      setError(null);
    }
  };

  const updateMessageFeedback = (
    messageId: string,
    feedback: { liked?: boolean; disliked?: boolean }
  ) => {
    if (!activeConversationId) return;
    setConversations((prev) => {
      const current = prev[activeConversationId];
      if (!current) return prev;
      const updatedMessages = current.messages.map((m) =>
        m.id === messageId
          ? {
              ...m,
              feedback: {
                liked: Boolean(feedback.liked),
                disliked: Boolean(feedback.disliked),
              },
            }
          : m
      );
      return {
        ...prev,
        [activeConversationId]: {
          ...current,
          messages: updatedMessages,
          updatedAt: new Date(),
        },
      };
    });
    // TODO: Optionally persist feedback to backend if/when endpoint exists
  };


  // Smart conversation naming function
  const generateSmartTitle = (messages: Message[]): string => {
    if (messages.length === 0) return 'New chat';
    
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length === 0) return 'New chat';
    
    const firstMessage = userMessages[0].content.trim();
    
    // Extract key topics and create a smart title
    const words = firstMessage.toLowerCase().split(/\s+/);
    
    // Common question words to remove
    const stopWords = new Set([
      'what', 'how', 'why', 'when', 'where', 'who', 'can', 'could', 'would', 'should',
      'is', 'are', 'was', 'were', 'do', 'does', 'did', 'will', 'have', 'has', 'had',
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with',
      'by', 'from', 'up', 'about', 'into', 'through', 'during', 'before', 'after',
      'above', 'below', 'between', 'among', 'i', 'you', 'he', 'she', 'it', 'we', 'they',
      'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'her', 'its', 'our', 'their'
    ]);
    
    // Filter out stop words and get meaningful words
    const meaningfulWords = words
      .filter(word => word.length > 2 && !stopWords.has(word))
      .slice(0, 6); // Take first 6 meaningful words
    
    if (meaningfulWords.length === 0) {
      // Fallback to truncated first message
      return firstMessage.length > 30 ? firstMessage.substring(0, 30) + '...' : firstMessage;
    }
    
    // Create title from meaningful words
    const title = meaningfulWords
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    // Ensure title isn't too long
    return title.length > 40 ? title.substring(0, 40) + '...' : title;
  };

  const value: ChatContextType = {
    conversations,
    activeConversationId,
    activeConversation,
    isConnected,
    isTyping,
    isLoading,
    error,
    sendMessage,
    newConversation,
    selectConversation,
    renameConversation,
    deleteConversation,
    clearMessages,
    clearAllConversations,
    updateMessageFeedback,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
